// ---------------------------------------------------------------------------
// Captura de erros e cache — usado pelo painel de Debug (site e painel).
//
// Guarda em memória os erros de JavaScript e as requisições que falharam,
// além de oferecer a limpeza de cache do navegador sem derrubar o login.
// ---------------------------------------------------------------------------

export type DebugEntryKind = "error" | "request" | "info";

export interface DebugEntry {
  id: number;
  kind: DebugEntryKind;
  message: string;
  detail: string;
  at: number;
}

const MAX_ENTRIES = 80;

let entries: DebugEntry[] = [];
let sequence = 0;
const listeners = new Set<() => void>();
let installed = false;

function emit(): void {
  for (const listener of listeners) listener();
}

export function recordDebug(kind: DebugEntryKind, message: string, detail = ""): void {
  sequence += 1;
  entries = [{ id: sequence, kind, message, detail, at: Date.now() }, ...entries].slice(0, MAX_ENTRIES);
  emit();
}

export function getDebugSnapshot(): DebugEntry[] {
  return entries;
}

export function clearDebugEntries(): void {
  entries = [];
  emit();
}

export function subscribeDebug(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function shortenUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

// Instala a captura global: erros de JS, promessas rejeitadas e requisições
// com falha (status >= 400 ou erro de rede). Idempotente.
export function installDebugCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error as Error | undefined;
    recordDebug("error", event.message || "Erro inesperado no site", err?.stack ?? "");
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { message?: string; stack?: string } | undefined;
    const message =
      reason?.message || (typeof event.reason === "string" ? event.reason : "Promessa rejeitada sem tratamento");
    recordDebug("error", message, reason?.stack ?? "");
  });

  const originalFetch: typeof window.fetch = window.fetch.bind(window);
  const debugFetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [input, init] = args;
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    try {
      const response = await originalFetch(...args);
      if (response.status >= 400) {
        recordDebug("request", `${method} ${response.status} — ${shortenUrl(url)}`, response.statusText);
      }
      return response;
    } catch (error) {
      recordDebug(
        "request",
        `${method} falhou — ${shortenUrl(url)}`,
        error instanceof Error ? error.message : String(error),
      );
      throw error;
    }
  };
  window.fetch = debugFetch as typeof window.fetch;
}

// Chaves que NÃO devem ser apagadas (sessão do painel).
const KEEP_KEYS = new Set(["real_admin_token", "real_admin_user"]);

// Limpa caches do navegador e dados temporários ligados ao site/painel,
// mantendo a sessão do administrador ativa. Devolve o resumo do que foi limpo.
export async function clearSiteCache(): Promise<string[]> {
  const cleared: string[] = [];

  if (typeof caches !== "undefined") {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
      if (keys.length) cleared.push(`${keys.length} cache(s) de arquivos`);
    } catch {
      // ignora
    }
  }

  try {
    const count = sessionStorage.length;
    sessionStorage.clear();
    if (count) cleared.push(`${count} item(ns) de sessão`);
  } catch {
    // ignora
  }

  try {
    const remove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && !KEEP_KEYS.has(key)) remove.push(key);
    }
    remove.forEach((key) => localStorage.removeItem(key));
    if (remove.length) cleared.push(`${remove.length} dado(s) guardado(s)`);
  } catch {
    // ignora
  }

  // Avisa as telas abertas para recarregarem aparência/redirecionamentos.
  window.dispatchEvent(new Event("theme-updated"));
  window.dispatchEvent(new Event("redirects-updated"));
  return cleared;
}